import { useImperativeHandle, useRef, useState } from 'react';
import type { FormEvent, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import { PhoneNumber } from '@ngsos/portal-core/incidents';
import { NgButton, NgModal } from '@/ui';
import type { NgModalHandle } from '@/ui';
import { useEditForm } from '@/ui/forms/useEditForm';
import { incidentDetailRoute } from '@/features/shared/routes';
import {
  createIncident,
  locateCallerForRequestedIncident,
  startChatWithCaller,
  startVideoChatWithCaller,
} from '@ngsos/portal-core/commands';

export type NewIncidentDialogAction = 'create' | 'locate' | 'chat' | 'videoChat';

const FORM_ID = 'new-incident-request-form';

export type NewIncidentDialogHandle = {
  showModal: (model?: { requestedIncidentId?: string; phoneNumber?: string }) => void;
};

export type NewIncidentDialogProps = {
  ref?: Ref<NewIncidentDialogHandle>;
};

export function NewIncidentDialog({ ref }: NewIncidentDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const agencyConfiguration = useAgencyConfiguration();
  const defaultCountryCode = (agencyConfiguration.agencyPortalConfiguration.defaultCountryCode as string | null) ?? '';

  const modalRef = useRef<NgModalHandle>(null);
  const phoneNumberInputRef = useRef<HTMLInputElement>(null);

  const [requestedIncidentId, setRequestedIncidentId] = useState<string>(() => crypto.randomUUID());
  const [phoneNumber, setPhoneNumber] = useState<string>(defaultCountryCode);
  const actionRef = useRef<NewIncidentDialogAction>('create');
  const createIncidentInProgressRef = useRef(false);
  const editForm = useEditForm<'phoneNumber'>();

  useImperativeHandle(ref, () => ({
    showModal: (model) => {
      setRequestedIncidentId(model?.requestedIncidentId ?? crypto.randomUUID());
      if (model?.phoneNumber !== undefined) {
        setPhoneNumber(model.phoneNumber);
      }
      modalRef.current?.show();
      editForm.reset({ phoneNumber: model?.phoneNumber ?? phoneNumber });
    },
  }));

  const validateModel = (action: NewIncidentDialogAction): { phoneNumber?: string[] } => {
    if ((action === 'create' && phoneNumber.length > 5) || action !== 'create') {
      try {
        new PhoneNumber(phoneNumber);
      } catch {
        return { phoneNumber: [t('NewIncidentDialogFormModel_Validate_PhoneNumberError')] };
      }
    }

    return {};
  };

  const getNumberCollection = (action: NewIncidentDialogAction): string[] => {
    if (action === 'create' && phoneNumber.length <= 5) {
      return [];
    }

    return [new PhoneNumber(phoneNumber).value];
  };

  const createIncidentOnEms = (action: NewIncidentDialogAction, phoneNumbers: string[]) => {
    switch (action) {
      case 'create':
        return createIncident(requestedIncidentId, phoneNumbers);
      case 'locate':
        return locateCallerForRequestedIncident(requestedIncidentId, phoneNumbers);
      case 'chat':
        return startChatWithCaller(requestedIncidentId, phoneNumbers);
      case 'videoChat':
        return startVideoChatWithCaller(requestedIncidentId, phoneNumbers);
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const action = actionRef.current;

    if (!editForm.validate(() => validateModel(action))) {
      return;
    }

    if (createIncidentInProgressRef.current) {
      return;
    }

    createIncidentInProgressRef.current = true;
    try {
      await createIncidentOnEms(action, getNumberCollection(action));
      void navigate(incidentDetailRoute(requestedIncidentId));
    } finally {
      createIncidentInProgressRef.current = false;
    }
  };

  const focusInput = () => {
    phoneNumberInputRef.current?.focus();
  };

  return (
    <NgModal
      ref={modalRef}
      header={t('NewIncidentDialog_Header')}
      onShown={focusInput}
      bodyTemplate={
        <form id={FORM_ID} onSubmit={(event) => void onSubmit(event)}>
          <div style={{ minHeight: '7rem' }}>
            <label htmlFor="phone-number" className="label">
              {t('NewIncidentDialog_Label_PhoneNumber')}
            </label>
            <input
              type="tel"
              id="phone-number"
              className={editForm.fieldCssClass('phoneNumber', 'form-control')}
              value={phoneNumber}
              onChange={(event) => {
                setPhoneNumber(event.target.value);
                editForm.notifyFieldInput('phoneNumber', event.target.value);
              }}
              onBlur={() => editForm.notifyFieldBlur('phoneNumber')}
              ref={phoneNumberInputRef}
            />
            {editForm.validationMessages('phoneNumber').map((message, index) => (
              <div key={index} className="validation-message">
                {message}
              </div>
            ))}
          </div>
        </form>
      }
      footerTemplate={
        <>
          <NgButton
            submitButton
            form={FORM_ID}
            icon="fal fa-plus-circle"
            text={t('NewIncidentDialog_Button_Create')}
            onClick={() => {
              actionRef.current = 'create';
            }}
          />

          <NgButton
            submitButton
            form={FORM_ID}
            icon="fal fa-location"
            text={t('NewIncidentDialog_Button_Localize')}
            onClick={() => {
              actionRef.current = 'locate';
            }}
          />

          <NgButton
            submitButton
            form={FORM_ID}
            icon="fal fa-messages"
            text={t('NewIncidentDialog_Button_CreateWithChat')}
            onClick={() => {
              actionRef.current = 'chat';
            }}
          />

          <NgButton
            submitButton
            form={FORM_ID}
            icon="fal fa-video-plus"
            text={t('NewIncidentDialog_Button_CreateWithVideo')}
            onClick={() => {
              actionRef.current = 'videoChat';
            }}
          />

          <NgButton text={t('Button_Cancel')} outline onClick={() => modalRef.current?.hide()} />
        </>
      }
    />
  );
}
